import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import type { LandingMetrics, MetricsOverview } from "@/lib/types";
import { DailyChart, PrizeList } from "@/components/MetricsBits";

const PERIODS = [
  { days: 1, label: "Сегодня" },
  { days: 7, label: "7 дней" },
  { days: 14, label: "14 дней" },
  { days: 30, label: "30 дней" },
  { days: 90, label: "90 дней" },
];

const fmt = (n: number) => n.toLocaleString("ru-RU");

export function Metrics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<MetricsOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    setData(null);
    api
      .metricsOverview(days)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [days]);

  const t = data?.totals;
  const anyData = data && data.landings.some((m) => m.visitors > 0 || m.page_views > 0);

  return (
    <div className="wrap">
      <h1>Метрики</h1>
      <p className="lead">Как работают ваши промо-лендинги: посетители, игры и выданные призы.</p>

      <div className="period">
        {PERIODS.map((p) => (
          <button key={p.days} className={days === p.days ? "on" : ""} onClick={() => setDays(p.days)}>
            {p.label}
          </button>
        ))}
      </div>

      {err && <div className="err">{err}</div>}
      {!data && !err && <div className="muted">Загрузка…</div>}

      {data && (
        <>
          <div className="kpis">
            <div className="kpi">
              <div className="v">{fmt(t!.visitors)}</div>
              <div className="l">Посетителей</div>
              <div className="s">уникальных за период</div>
            </div>
            <div className="kpi">
              <div className="v">{fmt(t!.game_starts)}</div>
              <div className="l">Сыграли</div>
              <div className="s">{Math.round(t!.play_rate * 100)}% от посетителей</div>
            </div>
            <div className="kpi">
              <div className="v">{fmt(t!.prizes_won)}</div>
              <div className="l">Выиграли приз</div>
              <div className="s">всего призов выдано</div>
            </div>
            <div className="kpi">
              <div className="v">{Math.round(t!.win_rate * 100)}%</div>
              <div className="l">Доля выигрышей</div>
              <div className="s">призов на запуск игры</div>
            </div>
          </div>

          {!anyData && (
            <div className="empty-note">
              Пока нет данных за этот период. Лендинги отправляют статистику автоматически —
              как только клиенты начнут заходить и играть, цифры появятся здесь.
            </div>
          )}

          <div className="msec">
            <h3>По дням</h3>
            <div className="card" style={{ padding: "16px 18px" }}>
              <DailyChart data={data.by_day} />
              <div className="legend">
                <span><i style={{ background: "var(--amber)" }} /> посетители</span>
                <span><i style={{ background: "var(--green)" }} /> доля выигравших приз</span>
              </div>
            </div>
          </div>

          <div className="msec">
            <h3>Выигранные призы</h3>
            <div className="card" style={{ padding: "16px 18px" }}>
              <PrizeList prizes={data.prizes} />
            </div>
          </div>

          <div className="msec">
            <h3>По лендингам</h3>
            <div className="card mtable">
              <div className="mrow head">
                <div>Лендинг</div>
                <div className="num">Посетители</div>
                <div className="num hidem">Сыграли</div>
                <div className="num hidem">Призы</div>
                <div className="num">Выигрыши</div>
              </div>
              {data.landings.map((m) => (
                <Row key={m.landing_id} m={m} onOpen={() => nav(`/site/${m.landing_id}`)} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ m, onOpen }: { m: LandingMetrics; onOpen: () => void }) {
  const peak = Math.max(1, ...m.by_day.map((d) => d.visitors));
  return (
    <div className="mrow">
      <div>
        <div className="nm" onClick={onOpen}>{m.name}</div>
        <div className="bars" title="посетители по дням">
          {m.by_day.length === 0 && <span className="small muted">нет визитов</span>}
          {m.by_day.map((d) => (
            <span
              key={d.date}
              className="bk"
              style={{ height: `${Math.round((d.visitors / peak) * 100)}%` }}
              title={`${d.date}: ${d.visitors} посетителей, ${d.prizes_won} призов`}
            />
          ))}
        </div>
      </div>
      <div className="num"><b>{fmt(m.visitors)}</b></div>
      <div className="num hidem">{fmt(m.game_starts)}</div>
      <div className="num hidem">{fmt(m.prizes_won)}</div>
      <div className="num">{Math.round(m.win_rate * 100)}%</div>
    </div>
  );
}
